"use client";

import { clsx } from "clsx";
import { Product } from "@repo/apis/browse-search-v2";
import { RecommendationProduct } from "@repo/apis/recommendations";
import { Link } from "@repo/next-utils/router";
import { AspectRatio } from "@repo/ui/aspect-ratio";
import { Box } from "@repo/ui/box";
import * as Flex from "@repo/ui/flex";
import * as List from "@repo/ui/list";
import * as ProductTile from "@repo/ui/product-tile";
import { SaveForLater } from "@repo/ui/saved-items";
import { ImageSizes } from "@repo/utils/image-presets";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import { CTA_REF_KEY } from "@/constants";
import { TYPOGRAPHY_LINK2_CAPS } from "@/theme/topshop/typography/typography-constants";
import { ProductLink } from "../product-link";
import { ProductTileColoursBadge } from "../product-tile-colours-badge/product-tile-colours-badge";
import classes from "./product-carousel.module.css";

export type ActionItem = {
  type: "action";
  id: string;
  title: string;
  href: string;
  onClick?: () => void;
};

export type CarouselItem = Product | RecommendationProduct | ActionItem;

export type ProductCarouselProps = {
  products:
    | Product[]
    | RecommendationProduct[]
    | CarouselItem[]
    | null
    | undefined;
  store: string;
  country: string;
  sourceSetProfile: ImageSizes;
  productTitleClassName?: string;
  productPriceClassName?: string;
  showProductInfo?: boolean;
  listItemStyle?: React.CSSProperties;
  currency: string;
  overlayProductInfo?: boolean;
  type?: "pab" | "ymal";
  onProductClick?: () => void;
  wrapper?: React.ComponentType<{ children: React.ReactElement }>;
  saveForLater?: boolean;
};

const isActionItem = (item: CarouselItem): item is ActionItem => {
  return "type" in item && item.type === "action";
};

const isProductItem = (
  item: CarouselItem,
): item is Product | RecommendationProduct => {
  return !isActionItem(item);
};

const ActionTile = ({
  item,
  listItemStyle,
  wrapper: Wrapper,
}: {
  item: ActionItem;
  listItemStyle?: React.CSSProperties;
  wrapper?: React.ComponentType<{ children: React.ReactElement }>;
}) => {
  const actionContent = (
    <Link
      href={item.href}
      onClick={item.onClick}
      className={classes.actionLink}
      aria-label={item.title}
    >
      <AspectRatio ratio={952 / 1215} className={classes.actionAspectRatio}>
        <Flex.Container justify="center" align="center">
          <ProductTile.Title className={TYPOGRAPHY_LINK2_CAPS}>
            {item.title}
          </ProductTile.Title>
        </Flex.Container>
      </AspectRatio>
    </Link>
  );

  return (
    <List.Item
      key={item.id}
      style={listItemStyle}
      className={classes.carouselItem}
    >
      <ProductTile.Root asChild>
        {Wrapper ? <Wrapper>{actionContent}</Wrapper> : actionContent}
      </ProductTile.Root>
    </List.Item>
  );
};

const renderProductTile = (
  product: Product | RecommendationProduct,
  {
    country,
    sourceSetProfile,
    productTitleClassName,
    productPriceClassName,
    listItemStyle,
    currency,
    showProductInfo,
    overlayProductInfo,
    type,
    onProductClick,
    wrapper: Wrapper,
    saveForLater,
  }: Omit<ProductCarouselProps, "products">,
) => {
  const productLink = (
    <ProductLink
      productName={product.name}
      productId={product.id}
      fragments={type && { [CTA_REF_KEY]: type }}
      onClick={onProductClick}
      className={classes.productLink}
    >
      <ProductTile.ProductImage
        imageUrl={prependUrlWithHttps(product.imageUrl)}
        altText={product.name}
        sourceSetProfile={sourceSetProfile}
        srcSize={168}
        className={classes.productImage}
      />

      {showProductInfo && (
        <Flex.Container className={classes.productContent} column>
          <ProductTile.Title className={productTitleClassName}>
            {product.name}
          </ProductTile.Title>
          <Flex.Container gap="xs" justify="space-between" align="start">
            <ProductTile.Price
              priceData={{
                currency: product.price.currency,
                current: product.price.current,
                previous: product.price.previous || {
                  text: "",
                  value: 0,
                },
                rrp: product.price.rrp || { text: "", value: 0 },
                isMarkedDown: product.price.isMarkedDown || false,
                isOutletPrice: product.price.isOutletPrice || false,
                discountPercentage: product.price.discountPercentage || 0,
              }}
              country={country}
              className={productPriceClassName}
              previousPriceCopy="Previous Price"
              currentPriceCopy="Current Price"
              currency={currency}
            />
            <Flex.Container gap="2xs" align="start">
              <ProductTileColoursBadge
                product={product}
                className={clsx(saveForLater && classes.coloursBadge)}
              />
              {saveForLater && (
                <SaveForLater
                  className={classes.saveForLater}
                  productId={Number(product.id)}
                  colourWayId={Number(product.colourWayId)}
                />
              )}
            </Flex.Container>
          </Flex.Container>
        </Flex.Container>
      )}

      {overlayProductInfo && (
        <Box className={classes.overlay} paddingX="sm" asChild>
          <Flex.Container justify="center" align="center" column gap="xs">
            <ProductTile.Title maxLines={2} className={productTitleClassName}>
              {product.name}
            </ProductTile.Title>
            <ProductTile.Price
              priceData={{
                currency: product.price.currency,
                current: product.price.current,
                previous: product.price.previous || {
                  text: "",
                  value: 0,
                },
                rrp: product.price.rrp || { text: "", value: 0 },
                isMarkedDown: product.price.isMarkedDown || false,
                isOutletPrice: product.price.isOutletPrice || false,
                discountPercentage: product.price.discountPercentage || 0,
              }}
              country={country}
              className={clsx(productPriceClassName, classes.productPrice)}
              previousPriceCopy="Previous Price"
              currentPriceCopy="Current Price"
              currency={currency}
            />
            {saveForLater && (
              <SaveForLater
                className={classes.saveForLater}
                productId={Number(product.id)}
                colourWayId={Number(product.colourWayId)}
              />
            )}
          </Flex.Container>
        </Box>
      )}
    </ProductLink>
  );

  return (
    <List.Item
      key={product.id}
      style={listItemStyle}
      className={classes.carouselItem}
    >
      <ProductTile.Root asChild>
        {Wrapper ? <Wrapper>{productLink}</Wrapper> : productLink}
      </ProductTile.Root>
    </List.Item>
  );
};

export const ProductCarousel = ({
  products,
  store,
  country,
  sourceSetProfile,
  productTitleClassName,
  productPriceClassName,
  listItemStyle,
  currency,
  showProductInfo = true,
  overlayProductInfo = false,
  type,
  onProductClick,
  wrapper: Wrapper,
  saveForLater = true,
}: ProductCarouselProps) => {
  if (!products || products.length < 4) return null;

  const listItems = products.map((item) => {
    if (isActionItem(item)) {
      return (
        <ActionTile
          key={item.id}
          item={item}
          listItemStyle={listItemStyle}
          wrapper={Wrapper}
        />
      );
    }

    if (isProductItem(item)) {
      return renderProductTile(item, {
        store,
        country,
        sourceSetProfile,
        productTitleClassName,
        productPriceClassName,
        listItemStyle,
        currency,
        showProductInfo,
        overlayProductInfo,
        type,
        onProductClick,
        wrapper: Wrapper,
        saveForLater,
      });
    }

    return null;
  });

  return listItems;
};
